import transformCompleteCatalogToEditor, {
  normalizeCatalogCriteriaComponents,
} from "../../../../utils/transformCompleteCatalogToEditor";
import { buildEvaluationFromExistingReport } from "../applyExistingReportToEvaluation";
import { INITIAL_EVALUATION_STATE } from "../../stores/EvaluationContext";

/** The first completed report for a product (the one that represents its published in-site ACR). */
export function findCompletedReport(reports) {
  return Array.isArray(reports)
    ? reports.find((report) => report?.completionStatus === "Completed")
    : null;
}

/**
 * Load the editor evaluation + catalog for a product's completed (in-site) ACR so it can be
 * exported (HTML / JSON / YAML) with the shared acrExport utilities. Shared by the product detail
 * page and the Product Listing bulk export so both build the export model the same way.
 *
 * Callers pass the API fetchers (kept injectable so the util stays framework-agnostic and testable)
 * and, when they already resolved it, the completed `report` to avoid a redundant lookup.
 *
 * @returns {Promise<{ok: boolean, reason?: string, evaluation?: object, catalog?: object}>}
 */
export default async function loadProductAcrExportModel({
  product,
  vendor,
  report,
  fetchReportsByProduct,
  getCompleteCatalogById,
  getConformanceLevels,
}) {
  if (product == null || typeof product !== "object" || !product.id) {
    return { ok: false, reason: "missing-product" };
  }

  try {
    let completedReport = report ?? null;
    if (!completedReport) {
      const reports = await fetchReportsByProduct(product.id);
      completedReport = findCompletedReport(reports);
    }
    if (!completedReport) {
      return { ok: false, reason: "no-report" };
    }

    const completeCatalog = await getCompleteCatalogById(completedReport.catalogId);
    if (!completeCatalog?.standards?.length) {
      return { ok: false, reason: "empty-catalog" };
    }

    let editorCatalog = transformCompleteCatalogToEditor(completeCatalog);
    const conformanceLevels = await getConformanceLevels();
    if (conformanceLevels.length > 0) {
      editorCatalog.conformanceLevels = conformanceLevels;
    }
    editorCatalog = normalizeCatalogCriteriaComponents(editorCatalog) ?? editorCatalog;

    const evaluation = buildEvaluationFromExistingReport({
      previousEvaluation: { ...INITIAL_EVALUATION_STATE },
      report: completedReport,
      productApi: product,
      vendorApi: vendor ?? product?.vendor ?? {},
      editorCatalog,
    });

    return { ok: true, evaluation, catalog: editorCatalog };
  } catch {
    return { ok: false, reason: "load-failed" };
  }
}
